import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle, Check, Copy, ExternalLink, KeyRound, Mail, Plus, RotateCw,
  ShieldCheck, Trash2, UserCircle2,
} from "lucide-react";

import { api } from "../api/client";
import { authenticated, login } from "../auth/keycloak";
import { Button, Input, Tag } from "../components/ds";
import type {
  ApiTokenSecret, ApiTokenSummary, Profile,
} from "../types/api";

type Toast = { kind: "ok" | "err"; message: string } | null;

function Section({
  icon: Icon, title, description, children,
}: {
  icon: typeof UserCircle2;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-sm border-[1.2px] border-mef-separator bg-white shadow-mef-card">
      <header className="flex items-start gap-3 border-b border-mef-separator px-4 py-3 sm:px-5 sm:py-4">
        <div className="h-9 w-9 shrink-0 rounded-xs bg-mef-green-tint text-mef-green-text inline-flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h2 className="mef-h3">{title}</h2>
          {description && (
            <p className="mt-0.5 text-[12px] leading-4 text-black/50">{description}</p>
          )}
        </div>
      </header>
      <div className="p-4 sm:p-5 space-y-4">{children}</div>
    </section>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function planLimitLabel(n: number, suffix: string): string {
  if (n < 0) return "Unlimited";
  if (n === 0) return "No cap";
  return `${n.toLocaleString()} ${suffix}`;
}

export default function ProfilePage() {
  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  // Local edits to the name/email form. Initialised from profile and reset
  // after each successful save.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);

  const [tokens, setTokens] = useState<ApiTokenSummary[] | null>(null);
  const [newTokenName, setNewTokenName] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  // Holds the cleartext that the server just returned. Cleared once the
  // user dismisses the banner — there is no way to retrieve it again.
  const [issued, setIssued] = useState<ApiTokenSecret | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAuthReady(authenticated());
    if (!authenticated()) return;
    api.profile.get().then((p) => {
      if (cancelled) return;
      setProfile(p);
      setFirstName(p.first_name ?? "");
      setLastName(p.last_name ?? "");
      setEmail(p.email ?? "");
    }).catch((e) => setLoadError((e as Error).message));
    api.profile.listTokens().then((r) => {
      if (!cancelled) setTokens(r.tokens);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const dirtyProfile = useMemo(() => {
    if (!profile) return false;
    return (firstName ?? "") !== (profile.first_name ?? "")
        || (lastName ?? "")  !== (profile.last_name ?? "")
        || (email ?? "")     !== (profile.email ?? "");
  }, [profile, firstName, lastName, email]);

  const emailChanged = profile && email !== profile.email;

  if (!authReady) {
    return (
      <div className="flex-1 grid place-items-center px-4 py-12">
        <div className="max-w-md rounded-sm border-[1.2px] border-mef-separator bg-white p-8 text-center shadow-mef-card">
          <UserCircle2 className="mx-auto h-10 w-10 text-mef-green-text" />
          <h1 className="mt-4 mef-h2">Sign in to manage your profile</h1>
          <p className="mt-2 text-[13px] leading-5 text-black/60">
            The profile page lets you update your name, change your password,
            see your active plan, and mint permanent API tokens.
          </p>
          <Button onClick={login} className="mt-6 w-full">
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex-1 grid place-items-center px-4 py-12">
        <div className="max-w-md rounded-sm border-[1.2px] border-red-300 bg-red-50 p-6 text-red-900 shadow-mef-card">
          <AlertCircle className="h-5 w-5" />
          <div className="mt-2 mef-h3 text-red-900">Couldn't load profile</div>
          <div className="mt-1 text-[12px] leading-5">{loadError}</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 grid place-items-center px-4 py-12 text-[13px] text-black/60">
        Loading profile…
      </div>
    );
  }

  async function saveProfile() {
    if (!profile) return;
    setSavingProfile(true);
    setToast(null);
    try {
      const updates: Parameters<typeof api.profile.update>[0] = {};
      if (firstName !== (profile.first_name ?? "")) updates.first_name = firstName;
      if (lastName !== (profile.last_name ?? ""))   updates.last_name  = lastName;
      if (email !== profile.email)                   updates.email      = email;
      const next = await api.profile.update(updates);
      setProfile(next);
      setFirstName(next.first_name ?? "");
      setLastName(next.last_name ?? "");
      setEmail(next.email ?? "");
      const msg = emailChanged
        ? "Profile updated. Check your inbox for a verification link before the new email becomes active."
        : "Profile updated.";
      setToast({ kind: "ok", message: msg });
    } catch (e) {
      setToast({ kind: "err", message: (e as Error).message || "Update failed" });
    } finally {
      setSavingProfile(false);
    }
  }

  async function requestPasswordReset() {
    setPwBusy(true);
    setToast(null);
    try {
      const r = await api.profile.requestPasswordReset(window.location.href);
      setToast({ kind: "ok", message: r.message });
    } catch (e) {
      setToast({ kind: "err", message: (e as Error).message || "Could not send password reset" });
    } finally {
      setPwBusy(false);
    }
  }

  async function requestVerifyEmail() {
    setVerifyBusy(true);
    setToast(null);
    try {
      const r = await api.profile.requestVerifyEmail();
      setToast({ kind: "ok", message: r.message });
    } catch (e) {
      setToast({ kind: "err", message: (e as Error).message || "Could not send verification email" });
    } finally {
      setVerifyBusy(false);
    }
  }

  async function createToken() {
    const name = newTokenName.trim() || "Untitled token";
    setTokenBusy(true);
    setToast(null);
    try {
      const t = await api.profile.createToken(name);
      setIssued(t);
      setNewTokenName("");
      const fresh = await api.profile.listTokens();
      setTokens(fresh.tokens);
    } catch (e) {
      setToast({ kind: "err", message: (e as Error).message || "Could not create token" });
    } finally {
      setTokenBusy(false);
    }
  }

  async function rotateToken(id: string) {
    setTokenBusy(true);
    setToast(null);
    try {
      const t = await api.profile.rotateToken(id);
      setIssued(t);
      const fresh = await api.profile.listTokens();
      setTokens(fresh.tokens);
    } catch (e) {
      setToast({ kind: "err", message: (e as Error).message || "Could not rotate token" });
    } finally {
      setTokenBusy(false);
    }
  }

  async function revokeToken(id: string) {
    setTokenBusy(true);
    setToast(null);
    try {
      await api.profile.revokeToken(id);
      const fresh = await api.profile.listTokens();
      setTokens(fresh.tokens);
      setToast({ kind: "ok", message: "Token revoked." });
    } catch (e) {
      setToast({ kind: "err", message: (e as Error).message || "Could not revoke token" });
    } finally {
      setTokenBusy(false);
      setConfirmingRevoke(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto bg-mef-subtle">
      <div className="mx-auto w-full max-w-3xl px-3 sm:px-6 py-5 sm:py-8 space-y-5 sm:space-y-6">
        <header>
          <h1 className="mef-h1">Profile</h1>
          <p className="mt-1 text-[13px] leading-5 text-black/60">
            Manage your account, see your active plan and quotas, and mint
            permanent API tokens for server-to-server calls.
          </p>
        </header>

        {toast && (
          <div
            className={[
              "rounded-xs border-[1.2px] px-4 py-3 text-[12px] leading-5 shadow-mef-card flex items-start gap-2",
              toast.kind === "ok"
                ? "border-mef-green/40 bg-mef-green-tint text-mef-green-text"
                : "border-red-300 bg-red-50 text-red-900",
            ].join(" ")}
          >
            {toast.kind === "ok" ? <Check className="h-4 w-4 mt-0.5 shrink-0" />
                                 : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
            <div className="flex-1">{toast.message}</div>
            <button
              onClick={() => setToast(null)}
              className="text-[11px] uppercase tracking-[0.04em] opacity-70 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Account section */}
        <Section
          icon={UserCircle2}
          title="Account"
          description="Changes are pushed to Keycloak; the local profile cache is kept in sync."
        >
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            <Input
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
            <Input
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </div>
          <Input
            label={
              <span className="inline-flex items-center gap-2">
                Email
                {profile.email_verified ? (
                  <Tag kind="completed">Verified</Tag>
                ) : (
                  <Tag kind="review">Unverified</Tag>
                )}
              </span>
            }
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            help={
              emailChanged
                ? "Saving will email a verification link to the new address. The old email stays active until the new one is confirmed."
                : "Used for sign-in and notifications."
            }
            autoComplete="email"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              onClick={saveProfile}
              disabled={!dirtyProfile || savingProfile}
            >
              {savingProfile ? "Saving…" : "Save changes"}
            </Button>
            {!profile.email_verified && (
              <Button
                variant="secondary"
                onClick={requestVerifyEmail}
                disabled={verifyBusy}
                leadingIcon={<Mail className="h-3.5 w-3.5" />}
              >
                {verifyBusy ? "Sending…" : "Send verification email"}
              </Button>
            )}
            {profile.account_console_url && (
              <a
                href={profile.account_console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-10 px-4 text-[11px] uppercase tracking-[0.02em] font-medium text-mef-green-text border-[1.2px] border-mef-green rounded-sm hover:bg-mef-green-tint min-w-[140px] justify-center mef-focus-ring"
              >
                Advanced settings
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </Section>

        {/* Password section */}
        <Section
          icon={ShieldCheck}
          title="Password"
          description="We never see or store your password — Keycloak emails you a one-time link to set a new one."
        >
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={requestPasswordReset}
              disabled={pwBusy}
              leadingIcon={<KeyRound className="h-3.5 w-3.5" />}
            >
              {pwBusy ? "Sending…" : "Email me a reset link"}
            </Button>
            <span className="text-[12px] leading-5 text-black/50">
              Goes to <strong className="text-black/70">{profile.email}</strong>.
            </span>
          </div>
        </Section>

        {/* Subscription / plan */}
        <Section
          icon={ShieldCheck}
          title="Subscription"
          description="Your current plan and how it shapes your API budgets."
        >
          <div className="rounded-xs border-[1.2px] border-mef-separator bg-mef-subtle p-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <div>
                <div className="mef-small-caption">Plan</div>
                <div className="mt-0.5 text-[18px] font-medium tracking-[0.01em] text-black/80">
                  {profile.plan_name ?? profile.plan}
                </div>
              </div>
              <div className="grow" />
              {profile.subscription_status && (
                <Tag kind={profile.subscription_status === "active" ? "completed" : "imported"}>
                  {profile.subscription_status}
                </Tag>
              )}
            </div>
            {profile.plan_description && (
              <p className="mt-2 text-[12px] leading-5 text-black/60">
                {profile.plan_description}
              </p>
            )}
            <dl className="mt-4 grid grid-cols-2 gap-3 sm:gap-6">
              <div>
                <dt className="mef-small-caption">Per-minute units</dt>
                <dd className="mt-0.5 text-[15px] font-medium tabular-nums text-black/80">
                  {planLimitLabel(profile.units_per_min, "units/min")}
                </dd>
              </div>
              <div>
                <dt className="mef-small-caption">Daily units</dt>
                <dd className="mt-0.5 text-[15px] font-medium tabular-nums text-black/80">
                  {planLimitLabel(profile.units_per_day, "units/day")}
                </dd>
              </div>
              <div>
                <dt className="mef-small-caption">Started</dt>
                <dd className="mt-0.5 text-[12px] tabular-nums text-black/70">
                  {formatDate(profile.subscription_started_at)}
                </dd>
              </div>
              <div>
                <dt className="mef-small-caption">Active modules</dt>
                <dd className="mt-0.5 text-[12px] text-black/70">
                  {profile.active_modules.length === 0
                    ? "None"
                    : profile.active_modules.join(", ")}
                </dd>
              </div>
            </dl>
          </div>
          <p className="text-[12px] leading-5 text-black/50">
            One unit ≈ one point lookup of one layer. Multi-layer / multi-year
            / zonal queries cost proportionally more — the API returns a{" "}
            <code className="font-mono text-[11px]">X-Cost</code> header on
            every response.
          </p>
        </Section>

        {/* API tokens */}
        <Section
          icon={KeyRound}
          title="API tokens"
          description="Permanent, non-expiring credentials for server-to-server calls. Rotate or revoke any time; usage counts toward your plan budgets exactly like a browser session."
        >
          {issued && (
            <div className="rounded-xs border-[1.2px] border-mef-green/40 bg-mef-green-tint p-3 sm:p-4">
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-mef-green-text shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium text-mef-green-text">
                    New token for <strong>{issued.name}</strong>
                  </div>
                  <p className="mt-1 text-[12px] leading-5 text-black/70">
                    This is the only time the full secret is shown. Copy it
                    now — we store only its hash.
                  </p>
                  <div className="mt-3 flex items-stretch gap-2">
                    <code className="flex-1 min-w-0 font-mono text-[12px] bg-white border-[1.2px] border-mef-separator rounded-xs px-2 py-2 break-all">
                      {issued.token}
                    </code>
                    <Button
                      size="sm"
                      onClick={() => {
                        navigator.clipboard?.writeText(issued.token).then(
                          () => setToast({ kind: "ok", message: "Copied to clipboard." }),
                          () => setToast({ kind: "err", message: "Clipboard blocked by browser." }),
                        );
                      }}
                      leadingIcon={<Copy className="h-3.5 w-3.5" />}
                      className="shrink-0"
                    >
                      Copy
                    </Button>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => setIssued(null)}
                      className="text-[11px] uppercase tracking-[0.04em] text-mef-green-text hover:text-black/80 mef-focus-ring"
                    >
                      I've copied it — dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-xs border-[1.2px] border-mef-separator bg-mef-subtle p-3 sm:p-4">
            <div className="mef-small-caption mb-2">Create new token</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder="e.g. Backend ingest job"
                className="!py-2"
              />
              <Button
                onClick={createToken}
                disabled={tokenBusy}
                leadingIcon={<Plus className="h-3.5 w-3.5" />}
              >
                Create token
              </Button>
            </div>
          </div>

          {tokens === null ? (
            <div className="text-[12px] text-black/50">Loading tokens…</div>
          ) : tokens.length === 0 ? (
            <div className="text-[12px] text-black/50">
              No tokens yet. Create one above for command-line or CI use.
            </div>
          ) : (
            <ul className="divide-y divide-mef-separator border-[1.2px] border-mef-separator rounded-xs">
              {tokens.map((t) => (
                <li key={t.id} className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[13px] text-black/80 truncate">
                        {t.name}
                      </span>
                      {t.revoked && <Tag kind="canceled">Revoked</Tag>}
                      {!t.revoked && t.rotated_at && <Tag kind="imported">Rotated</Tag>}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-black/50 truncate">
                      {t.prefix}…
                    </div>
                    <div className="mt-1 text-[11px] text-black/40">
                      Created {formatDate(t.created_at)} · Last used {formatDate(t.last_used_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {!t.revoked && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => rotateToken(t.id)}
                        disabled={tokenBusy}
                        leadingIcon={<RotateCw className="h-3.5 w-3.5" />}
                      >
                        Rotate
                      </Button>
                    )}
                    {!t.revoked && (
                      confirmingRevoke === t.id ? (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => revokeToken(t.id)}
                          disabled={tokenBusy}
                        >
                          Confirm revoke
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmingRevoke(t.id)}
                          leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
                        >
                          Revoke
                        </Button>
                      )
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="text-[12px] leading-5 text-black/50">
            Send the token in the <code className="font-mono text-[11px]">Authorization: Bearer …</code>{" "}
            header. Same rate-limits and quotas as your Keycloak session.
          </p>
        </Section>
      </div>
    </div>
  );
}
